/**
 * components/quotes/Quotes.tsx — Contenitore delle quote di scena (T-quote v1).
 *
 * MUTO: deriva le quote di default dalla `ResolvedScene` (`model/quotes.ts`),
 * le concatena con eventuali quote utente future (`userQuotes` — seam di
 * estensibilita', non ancora popolato da una UI), filtra per visibilita'
 * dell'elemento proprietario e mappa ciascuna sul componente di render
 * corretto in base a `kind`. Nessuna dipendenza viewer-only (niente
 * OrbitControls/preset): lo stesso componente serve il viewer interattivo e
 * la capture PDF (`LockSceneCapture.tsx`).*/

import { useMemo } from "react";
import type { ResolvedScene } from "../../model/types";
import { deriveDefaultQuotes } from "../../model/quotes";
import type { Quote, QuotePlane } from "../../model/quotes.types";
import { AngularDimension } from "./AngularDimension";
import { LinearDimension } from "./LinearDimension";

export interface QuotesProps {
  /** Scena risolta (SSOT geometria) da cui derivare le quote di default.*/
  scene: ResolvedScene;
  /** Quote aggiunte dall'utente (futuro — nessuna UI di picking in v1).*/
  userQuotes?: Quote[];
  /**
   * Visibilita' per `ownerId` (es. "beam:joist" → false quando la trave e'
   * nascosta). Una quota senza `ownerId`, o il cui owner non compare nella
   * mappa, resta sempre visibile.*/
  visibility?: Record<string, boolean>;
  /**
   * SOLO EXPORT PDF (30-09-2026): vista del report per cui si sta generando
   * l'immagine — filtra a `quote.planes.includes(exportPlane)` per non
   * stampare quote degeneri (un segmento parallelo all'asse di vista appare
   * come un punto). Il viewer interattivo NON lo passa mai: li' tutte le
   * quote esistono nello spazio 3D qualunque sia l'orientamento della camera.
   * `undefined` → nessun filtro.*/
  exportPlane?: QuotePlane;
  /**
   * Id delle quote da NON disegnare (30-09-2026): nelle viste del report
   * quelle fuori inquadratura o di un'altra vista; nel viewer e nella
   * perspective le copie per vista (`PER_VIEW_COPY_QUOTE_IDS`), che
   * altrimenti si vedrebbero doppie. `undefined` → nessun filtro.*/
  excludeIds?: readonly string[];
  /**
   * SOLO EXPORT PDF (30-09-2026): se presente, stampa SOLO queste quote (es.
   * le viste laterali, che mostrano solo le distanze connettore → trave).*/
  includeIds?: readonly string[];
  /** Visibilita' del gruppo intero (toggle "Quotes" del menu Elements).*/
  visible?: boolean;
}

/** Render dell'intero set di quote (default + utente), filtrato per visibilita' e (solo export) vista.*/
export function Quotes({
  scene,
  userQuotes,
  visibility,
  exportPlane,
  excludeIds,
  includeIds,
  visible = true,
}: QuotesProps) {
  const quotes = useMemo(() => {
    let all = [...deriveDefaultQuotes(scene), ...(userQuotes ?? [])];
    if (visibility) all = all.filter((q) => q.ownerId === undefined || (visibility[q.ownerId] ?? true));
    if (exportPlane) all = all.filter((q) => q.planes.includes(exportPlane));
    if (excludeIds) all = all.filter((q) => !excludeIds.includes(q.id));
    if (includeIds) all = all.filter((q) => includeIds.includes(q.id));
    return all;
  }, [scene, userQuotes, visibility, exportPlane, excludeIds, includeIds]);

  return (
    <group visible={visible}>
      {quotes.map((quote) =>
        quote.kind === "linear" ? (
          <LinearDimension key={quote.id} quote={quote} />
        ) : (
          <AngularDimension key={quote.id} quote={quote} />
        ),
      )}
    </group>
  );
}
